import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check } from 'lucide-react';
import { Product, Consideration, SelectedConsideration } from '../../types/pos.types';

interface ConsiderationSelectorProps {
  isOpen: boolean;
  product: Product | null;
  considerations: Consideration[];
  onClose: () => void;
  onConfirm: (product: Product, selectedConsiderations: SelectedConsideration[]) => void;
}

export const ConsiderationSelector: React.FC<ConsiderationSelectorProps> = ({
  isOpen,
  product,
  considerations,
  onClose,
  onConfirm
}) => {
  const [selectedConsiderations, setSelectedConsiderations] = useState<SelectedConsideration[]>([]);

  const handleToggleConsideration = (consideration: Consideration) => {
    setSelectedConsiderations(prev => {
      const isSelected = prev.some(item => item.id === consideration.id);

      if (isSelected) {
        // Deseleccionar: remover del array
        return prev.filter(item => item.id !== consideration.id);
      } else {
        // Seleccionar: agregar al array
        return [...prev, { id: consideration.id, name: consideration.name }];
      }
    });
  };

  const isSelected = (considerationId: number): boolean => {
    return selectedConsiderations.some(item => item.id === considerationId);
  };

  const handleConfirm = () => {
    if (product) {
      onConfirm(product, selectedConsiderations);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedConsiderations([]);
    onClose();
  };

  const handleSkip = () => {
    if (product) {
      onConfirm(product, []);
      handleClose();
    }
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            ¿Alguna consideración especial?
          </DialogTitle>
          <div className="text-sm text-gray-600 mt-2">
            <span className="font-medium">{product.name}</span>
            {product.presentationData && (
              <span> - {product.presentationData.name}</span>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Lista de consideraciones disponibles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {considerations.map(consideration => {
              const selected = isSelected(consideration.id);

              return (
                <Card
                  key={consideration.id}
                  onClick={() => handleToggleConsideration(consideration)}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md active:scale-95 ${
                    selected
                      ? 'ring-2 ring-macartur-primary border-macartur-primary bg-macartur-primary/5'
                      : 'hover:border-gray-300 border-2 border-gray-200'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col items-center text-center gap-2">
                      {/* Indicador de selección */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 ${
                        selected
                          ? 'bg-macartur-primary text-white'
                          : 'bg-gray-100 text-gray-400'
                      }`}>
                        {selected && <Check className="h-4 w-4" />}
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">
                          {consideration.name}
                        </h4>
                        {consideration.description && (
                          <p className="text-xs text-gray-500 mt-1">
                            {consideration.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Mostrar consideraciones seleccionadas */}
          {selectedConsiderations.length > 0 && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Seleccionadas:</p>
              <div className="flex flex-wrap gap-2">
                {selectedConsiderations.map(item => (
                  <span
                    key={item.id}
                    className="px-3 py-1 bg-macartur-primary text-white text-sm rounded-full"
                  >
                    {item.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Botones de acción - Touch friendly */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleSkip}
            className="flex-1 h-14 text-base font-semibold border-2 transition-all duration-200 active:scale-95"
          >
            Omitir consideraciones
          </Button>
          <Button
            onClick={handleConfirm}
            className="flex-1 h-14 text-base font-semibold bg-macartur-primary hover:bg-macartur-primary/90 transition-all duration-200 active:scale-95"
          >
            Confirmar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
